import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: React.ReactNode;
  requiredRole?: "buyer" | "seller" | "provider" | "admin";
  requiredAccountType?: "personal" | "company" | "admin";
}

export function ProtectedRoute({ children, requiredRole, requiredAccountType }: Props) {
  const { user, loading, hasRole, profile, rolesLoaded } = useAuth();
  const needsAdminAal = requiredRole === "admin";
  const [aalState, setAalState] = useState<"checking" | "ok" | "needs-2fa" | "needs-enroll">("checking");

  useEffect(() => {
    if (!needsAdminAal || !user || !rolesLoaded || !hasRole("admin")) {
      setAalState("ok");
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (cancelled) return;
      if (aal?.currentLevel === "aal2") { setAalState("ok"); return; }
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const hasVerified = !!factors?.totp?.find((f) => f.status === "verified");
      if (cancelled) return;
      setAalState(hasVerified ? "needs-2fa" : "needs-enroll");
    })();
    return () => { cancelled = true; };
  }, [needsAdminAal, user, rolesLoaded, hasRole]);

  const stillLoading = loading || (!!user && requiredRole && !rolesLoaded) || (needsAdminAal && aalState === "checking");

  if (stillLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/logga-in" replace />;
  if (requiredRole && !hasRole(requiredRole)) return <Navigate to="/" replace />;
  if (requiredAccountType && profile?.account_type !== requiredAccountType) {
    return <Navigate to="/dashboard" replace />;
  }
  if (needsAdminAal && aalState === "needs-2fa") return <Navigate to="/verifiera-2fa" replace />;
  if (needsAdminAal && aalState === "needs-enroll") return <Navigate to="/sakerhet/tva-faktor" replace />;

  return <>{children}</>;
}
